import React, { useState } from 'react';
import { Account, TransactionType, TransactionDetail, Transaction } from '../types';
import { createTransaction, createTransfer, updateTransaction, deleteTransaction, checkTransactionConnections } from '../services/financeService';
import { useFirebase } from './FirebaseProvider';
import { X, Plus, Trash2, Save, ArrowRightLeft, AlertTriangle, Link as LinkIcon, Lock } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { getInstitutionById } from '../constants/institutions';
import { useClosingDate } from '../hooks/useClosingDate';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

interface TransactionFormProps {
  type: TransactionType;
  accounts: Account[];
  onClose: () => void;
  editTransaction?: Transaction;
}

const getLocalDateString = (d: Date) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const TransactionForm: React.FC<TransactionFormProps> = ({ type, accounts, onClose, editTransaction }) => {
  const { user, effectiveUserId } = useFirebase();
  const [description, setDescription] = useState(editTransaction?.description || '');
  const [date, setDate] = useState(editTransaction?.date ? getLocalDateString(new Date(editTransaction.date)) : getLocalDateString(new Date()));
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [connections, setConnections] = useState<{ type: 'PLANNING' | 'MANAGEMENT', id: string, name?: string }[]>([]);
  const [checkingConnections, setCheckingConnections] = useState(false);
  const closingDate = useClosingDate(effectiveUserId);
  
  const isLocked = date <= closingDate;
  const isEditLocked = !!(editTransaction && editTransaction.date <= closingDate);

  React.useEffect(() => {
    const fetchConnections = async () => {
      if (editTransaction && effectiveUserId) {
        setCheckingConnections(true);
        const conns = await checkTransactionConnections(effectiveUserId, editTransaction.id);
        setConnections(conns);
        setCheckingConnections(false);
      }
    };
    fetchConnections();
  }, [editTransaction, effectiveUserId]);

  // For Income/Expense splits
  const [details, setDetails] = useState<TransactionDetail[]>(
    editTransaction?.details || [
      { accountId: accounts[0]?.id || '', amount: 0, type: type === 'INCOME' ? 'CREDIT' : 'DEBIT' }
    ]
  );

  // For Transfer
  const [fromAccountId, setFromAccountId] = useState(
    editTransaction?.type === 'TRANSFER' ? editTransaction.details.find(d => d.type === 'DEBIT')?.accountId || accounts[0]?.id || '' : accounts[0]?.id || ''
  );
  const [toAccountId, setToAccountId] = useState(
    editTransaction?.type === 'TRANSFER' ? editTransaction.details.find(d => d.type === 'CREDIT')?.accountId || accounts[1]?.id || '' : accounts[1]?.id || ''
  );
  const [transferAmount, setTransferAmount] = useState(
    editTransaction?.type === 'TRANSFER' ? editTransaction.details[0]?.amount || 0 : 0
  );

  const handleAddDetail = () => {
    setDetails([...details, { accountId: accounts[0]?.id || '', amount: 0, type: type === 'INCOME' ? 'CREDIT' : 'DEBIT' }]);
  };

  const handleRemoveDetail = (index: number) => {
    setDetails(details.filter((_, i) => i !== index));
  };

  const handleDetailChange = (index: number, field: keyof TransactionDetail, value: any) => {
    const newDetails = [...details];
    newDetails[index] = { ...newDetails[index], [field]: value };
    setDetails(newDetails);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!effectiveUserId || connections.length > 0) return;
    setLoading(true);

    try {
      const finalDetails = type === 'TRANSFER' 
        ? [
            { accountId: fromAccountId, amount: transferAmount, type: 'DEBIT' as const },
            { accountId: toAccountId, amount: transferAmount, type: 'CREDIT' as const }
          ]
        : details.filter(d => d.amount > 0);

      const dateToSave = new Date(`${date}T12:00:00`).toISOString();

      if (editTransaction) {
        await updateTransaction(effectiveUserId, editTransaction, {
          description,
          type,
          date: dateToSave,
          details: finalDetails
        });
      } else {
        if (type === 'TRANSFER') {
          await createTransfer(effectiveUserId, {
            description: description || 'Transferencia',
            date: dateToSave,
            fromAccountId,
            toAccountId,
            amount: transferAmount
          });
        } else {
          await createTransaction(effectiveUserId, {
            description,
            type,
            date: dateToSave,
            details: finalDetails
          });
        }
      }
      onClose();
    } catch (err) {
      console.error('Error saving transaction:', err);
      alert('Error al guardar la transacción');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!effectiveUserId || !editTransaction || connections.length > 0) return;
    setLoading(true);
    try {
      await deleteTransaction(effectiveUserId, editTransaction);
      onClose();
    } catch (err) {
      console.error('Error deleting transaction:', err);
      alert('Error al eliminar la transacción');
    } finally {
      setLoading(false);
    }
  };

  const totalAmount = type === 'TRANSFER' ? transferAmount : details.reduce((sum, d) => sum + d.amount, 0);

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-zinc-900 border border-zinc-800 w-full max-w-xl rounded-2xl shadow-2xl overflow-hidden"
      >
        <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
          <h2 className="text-xl font-bold flex items-center gap-2">
            {editTransaction ? 'Editar Registro' : (type === 'INCOME' ? 'Registrar Ingreso' : type === 'EXPENSE' ? 'Registrar Gasto' : 'Nueva Transferencia')}
          </h2>
          <div className="flex items-center gap-2">
            {editTransaction && !isLocked && (
              <button 
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="p-2 hover:bg-red-500/10 rounded-lg transition-all text-red-500"
              >
                <Trash2 size={20} />
              </button>
            )}
            <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg transition-all text-zinc-500 hover:text-white">
              <X size={20} />
            </button>
          </div>
        </div>

        {showDeleteConfirm ? (
          <div className="p-12 text-center space-y-6">
            <div className={`w-16 h-16 ${connections.length > 0 ? 'bg-amber-500/10 border-amber-500/20' : 'bg-red-500/10 border-red-500/20'} rounded-full flex items-center justify-center mx-auto border`}>
              <AlertTriangle className={connections.length > 0 ? 'text-amber-500' : 'text-red-500'} size={32} />
            </div>
            <div>
              <h3 className="text-lg font-bold">
                {connections.length > 0 ? 'No se puede eliminar' : '¿Eliminar transacción?'}
              </h3>
              <p className="text-zinc-500 text-sm mt-2">
                {connections.length > 0 
                  ? 'Este movimiento está vinculado a otros procesos. Debes eliminar las referencias primero.' 
                  : 'Esta acción revertirá los saldos de las cuentas afectadas.'}
              </p>

              {connections.length > 0 && (
                <div className="mt-4 p-4 bg-zinc-950 rounded-xl border border-zinc-800 text-left space-y-2">
                  <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest px-1">Vínculos detectados:</p>
                  {connections.map((conn, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-xs text-amber-400 bg-amber-500/5 p-2 rounded-lg border border-amber-500/10">
                      <LinkIcon size={12} />
                      <span className="font-bold">{conn.type === 'PLANNING' ? 'Flujo de Caja' : 'Gestión'}</span>
                      {conn.name && <span className="opacity-60">• {conn.name}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-3">
              <button 
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-6 py-3 bg-zinc-800 hover:bg-zinc-700 rounded-xl font-bold transition-all"
              >
                {connections.length > 0 ? 'Entendido' : 'Cancelar'}
              </button>
              {connections.length === 0 && (
                <button 
                  onClick={handleDelete}
                  disabled={loading}
                  className="flex-1 px-6 py-3 bg-red-500 hover:bg-red-600 text-black rounded-xl font-bold transition-all flex items-center justify-center gap-2"
                >
                  {loading ? <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" /> : 'Eliminar'}
                </button>
              )}
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {isLocked && (
              <div className="bg-red-500/10 border border-red-500/20 p-4 rounded-xl flex gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
                <Lock className="text-red-400 shrink-0" size={20} />
                <div className="space-y-1">
                  <p className="text-sm font-bold text-red-500">Operación Bloqueada</p>
                  <p className="text-xs text-red-400/80 leading-relaxed">
                    La fecha corresponde a un periodo cerrado. No puedes editar ni eliminar esta operación.
                  </p>
                </div>
              </div>
            )}
            
            {connections.length > 0 && (
              <div className="bg-amber-500/5 border border-amber-500/20 p-4 rounded-xl flex gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
                <AlertTriangle className="text-amber-500 shrink-0" size={20} />
                <div className="space-y-1">
                  <p className="text-sm font-bold text-amber-500">Movimiento Vinculado</p>
                  <p className="text-xs text-zinc-400 leading-relaxed">
                    Este registro está asociado a <span className="text-zinc-200 font-medium">Planificación o Gestión</span>. Para evitar inconsistencias, no se permite su edición o borrado hasta que se retiren las referencias.
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {connections.map((conn, idx) => (
                      <div key={idx} className="text-[10px] font-bold bg-amber-500/10 text-amber-500 px-2 py-1 rounded-md border border-amber-500/20 uppercase tracking-tighter">
                        {conn.type === 'PLANNING' ? 'Planificación' : conn.name || 'Gestión'}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 md:col-span-1">
                <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2">Fecha</label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className={`w-full bg-zinc-950 border ${isLocked ? 'border-red-500/30' : 'border-zinc-800'} rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500/50 outline-none`}
                  required
                  disabled={isEditLocked}
                />
              </div>
              <div className="col-span-2 md:col-span-1">
                <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2">Descripción</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={type === 'TRANSFER' ? 'Transferencia entre cuentas' : 'Ej: Pago de servicios'}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500/50 outline-none"
                  required
                  disabled={isEditLocked}
                />
              </div>
            </div>

            {type === 'TRANSFER' ? (
              <div className="space-y-4 bg-zinc-950/50 p-4 rounded-xl border border-zinc-800/50">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2 text-red-500/80">Desde (Origen)</label>
                    <select
                      value={fromAccountId}
                      onChange={(e) => setFromAccountId(e.target.value)}
                      className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2.5 text-sm outline-none"
                      disabled={isEditLocked}
                    >
                      {accounts.map(acc => {
                        const instName = acc.institutionId ? getInstitutionById(acc.institutionId)?.name : '';
                        const displayName = instName ? `${acc.name} - ${instName}` : acc.name;
                        return (
                          <option key={acc.id} value={acc.id}>{displayName} ({new Intl.NumberFormat('es-PY').format(acc.balance)})</option>
                        );
                      })}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2 text-emerald-500/80">Hacia (Destino)</label>
                    <select
                      value={toAccountId}
                      onChange={(e) => setToAccountId(e.target.value)}
                      className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2.5 text-sm outline-none"
                      disabled={isEditLocked}
                    >
                      {accounts.map(acc => {
                        const instName = acc.institutionId ? getInstitutionById(acc.institutionId)?.name : '';
                        const displayName = instName ? `${acc.name} - ${instName}` : acc.name;
                        return (
                          <option key={acc.id} value={acc.id}>{displayName} ({new Intl.NumberFormat('es-PY').format(acc.balance)})</option>
                        );
                      })}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2">Monto a Transferir</label>
                  <input
                    type="number"
                    value={transferAmount}
                    onChange={(e) => setTransferAmount(Number(e.target.value))}
                    className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2.5 text-sm font-mono text-xl text-center"
                    required
                    disabled={isEditLocked}
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="block text-xs font-bold text-zinc-500 uppercase tracking-wider">Detalle de Cuentas</label>
                  <button 
                    type="button" 
                    onClick={() => { if (!isEditLocked) handleAddDetail() }}
                    disabled={isEditLocked}
                    className={`text-xs font-bold flex items-center gap-1 ${isEditLocked ? 'text-zinc-600' : 'text-emerald-500 hover:text-emerald-400'}`}
                  >
                    <Plus size={14} /> Agregar Cuenta
                  </button>
                </div>
                
                <div className="space-y-3 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                  {details.map((detail, index) => (
                    <div key={index} className="flex gap-3 items-end group">
                      <div className="flex-1">
                        <select
                          value={detail.accountId}
                          onChange={(e) => handleDetailChange(index, 'accountId', e.target.value)}
                          className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2.5 text-sm outline-none"
                          disabled={isEditLocked}
                        >
                          {accounts.map(acc => {
                            const instName = acc.institutionId ? getInstitutionById(acc.institutionId)?.name : '';
                            const displayName = instName ? `${acc.name} - ${instName}` : acc.name;
                            return (
                              <option key={acc.id} value={acc.id}>{displayName}</option>
                            );
                          })}
                        </select>
                      </div>
                      <div className="w-32">
                        <input
                          type="number"
                          value={detail.amount}
                          onChange={(e) => handleDetailChange(index, 'amount', Number(e.target.value))}
                          className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2.5 text-sm font-mono"
                          placeholder="Monto"
                          required
                          disabled={isEditLocked}
                        />
                      </div>
                      {details.length > 1 && (
                        <button 
                          type="button" 
                          onClick={() => handleRemoveDetail(index)}
                          disabled={isEditLocked}
                          className="p-2.5 text-zinc-600 hover:text-red-500 transition-all disabled:opacity-0"
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="pt-6 border-t border-zinc-800 flex items-center justify-between">
              <div>
                <div className="text-xs text-zinc-500 uppercase font-bold tracking-widest">Total Operación</div>
                <div className={cn(
                  "text-2xl font-bold",
                  type === 'INCOME' ? 'text-emerald-500' : type === 'EXPENSE' ? 'text-red-500' : 'text-zinc-100'
                )}>
                  {new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG' }).format(totalAmount)}
                </div>
              </div>
              <button
                type="submit"
                disabled={loading || totalAmount <= 0 || connections.length > 0 || isLocked}
                className={cn(
                  "px-8 py-3 rounded-xl font-bold flex items-center gap-2 transition-all disabled:opacity-50",
                  type === 'INCOME' ? 'bg-emerald-500 text-black hover:bg-emerald-600' : 
                  type === 'EXPENSE' ? 'bg-red-500 text-black hover:bg-red-600' : 
                  'bg-zinc-100 text-black hover:bg-white'
                )}
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                ) : (
                  <>
                    <Save size={18} />
                    {editTransaction ? 'Actualizar' : 'Guardar'}
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
};
